import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, Sparkles } from "lucide-react";
import Layout from "../components/layout/Layout";
import ProductCard from "../components/ProductCard";
import EmptyState from "../components/EmptyState";
import { SkeletonGrid } from "../components/SkeletonCard";
import { api } from "../api/client";

const SORTS = [
    { value: "", label: "Featured" },
    { value: "price_asc", label: "Price: Low → High" },
    { value: "price_desc", label: "Price: High → Low" },
    { value: "name", label: "Name (A–Z)" },
];

const Products = () => {
    const [params, setParams] = useSearchParams();
    const category = params.get("category") || "";
    const condition = params.get("condition") || "";
    const q = params.get("q") || "";
    const sort = params.get("sort") || "";
    const page = parseInt(params.get("page") || "1", 10);
    const rxFilter = params.get("rx") || (params.get("rx_only") === "true" ? "rx" : params.get("rx_only") === "false" ? "non-rx" : "");
    const smart = params.get("smart") === "true";

    const [cats, setCats] = useState([]);
    const [conditions, setConditions] = useState([]);
    const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState(q);

    useEffect(() => {
        api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
        api.get("/conditions").then(({ data }) => setConditions(data.items || [])).catch(() => {});
    }, []);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const queryParams = {
                    page,
                    page_size: 12,
                    ...(category ? { category } : {}),
                    ...(condition ? { condition } : {}),
                    ...(q ? { q } : {}),
                    ...(sort ? { sort } : {}),
                    ...(rxFilter === "rx" ? { rx_only: true } : rxFilter === "non-rx" ? { rx_only: false } : {}),
                    ...(smart && q ? { smart: true } : {}),
                };
                const { data } = await api.get("/products", { params: queryParams });
                if (mounted) setData(data);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [category, condition, q, sort, page, rxFilter, smart]);

    useEffect(() => { setSearchInput(q); }, [q]);

    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value === "" || value === null || value === undefined) next.delete(key);
        else next.set(key, value);
        if (key !== "page") next.delete("page");
        setParams(next, { replace: false });
    };

    const onSearchSubmit = (e) => {
        e.preventDefault();
        setParam("q", searchInput.trim());
    };

    const totalPages = useMemo(() => Math.max(1, Math.ceil(data.total / (data.page_size || 12))), [data]);
    const activeCategory = cats.find((c) => c.slug === category);

    return (
        <Layout>
            <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12" data-testid="products-page">
                <header className="mb-8">
                    <p className="label-uppercase">Catalog</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
                        {activeCategory ? activeCategory.name : "All products"}
                    </h1>
                    <p className="mt-2 text-sm text-zinc-500">{data.total} item{data.total === 1 ? "" : "s"}</p>
                </header>

                <div className="grid gap-8 lg:grid-cols-[260px_1fr]">
                    <aside className="space-y-6" data-testid="products-filter-sidebar">
                        <form onSubmit={onSearchSubmit} className="relative">
                            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                            <input
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search products..."
                                className="input-clean pl-10"
                                data-testid="products-search-input"
                            />
                        </form>

                        <div>
                            <p className="label-uppercase mb-3">Categories</p>
                            <div className="space-y-1">
                                <button
                                    type="button"
                                    onClick={() => setParam("category", "")}
                                    className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${!category ? "bg-brand-50 text-brand-700" : "text-zinc-700 hover:bg-zinc-50"}`}
                                    data-testid="filter-category-all"
                                >
                                    All products
                                </button>
                                {cats.map((c) => (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => setParam("category", c.slug)}
                                        className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${category === c.slug ? "bg-brand-50 text-brand-700" : "text-zinc-700 hover:bg-zinc-50"}`}
                                        data-testid={`filter-category-${c.slug}`}
                                    >
                                        {c.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <p className="label-uppercase mb-3">Type</p>
                            <div className="flex flex-wrap gap-2">
                                <button onClick={() => setParam("rx", "")} className={`chip ${!rxFilter ? "chip-active" : ""}`} data-testid="filter-rx-any">Any</button>
                                <button onClick={() => setParam("rx", "non-rx")} className={`chip ${rxFilter === "non-rx" ? "chip-active" : ""}`} data-testid="filter-rx-non-rx">No Rx</button>
                                <button onClick={() => setParam("rx", "rx")} className={`chip ${rxFilter === "rx" ? "chip-active" : ""}`} data-testid="filter-rx-rx">Rx only</button>
                            </div>
                        </div>

                        <div>
                            <p className="label-uppercase mb-3">By condition</p>
                            <div className="flex flex-wrap gap-1.5" data-testid="filter-condition-chips">
                                <button
                                    onClick={() => setParam("condition", "")}
                                    className={`chip ${!condition ? "chip-active" : ""}`}
                                    data-testid="filter-condition-all"
                                >
                                    All
                                </button>
                                {conditions.slice(0, 16).map((c) => (
                                    <button
                                        key={c.slug}
                                        onClick={() => setParam("condition", c.slug)}
                                        className={`chip ${condition === c.slug ? "chip-active" : ""}`}
                                        data-testid={`filter-condition-${c.slug}`}
                                        title={c.short_description}
                                    >
                                        <span className="mr-1" aria-hidden>{c.icon_emoji}</span>{c.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {(category || condition || q || sort || rxFilter) && (
                            <button
                                onClick={() => setParams({}, { replace: false })}
                                className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-brand-700"
                                data-testid="filter-clear-btn"
                            >
                                <X className="h-3 w-3" /> Clear filters
                            </button>
                        )}
                    </aside>

                    <div>
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex flex-wrap items-center gap-2">
                                {category && (
                                    <span className="chip chip-active" data-testid="active-category-chip">
                                        {activeCategory?.name || category}
                                        <button onClick={() => setParam("category", "")} aria-label="Remove" className="ml-1 text-brand-700">×</button>
                                    </span>
                                )}
                                {condition && (
                                    <span className="chip chip-active" data-testid="active-condition-chip">
                                        {conditions.find((c) => c.slug === condition)?.name || condition}
                                        <button onClick={() => setParam("condition", "")} aria-label="Remove" className="ml-1 text-brand-700">×</button>
                                    </span>
                                )}
                                {q && (
                                    <span className="chip chip-active">
                                        "{q}"
                                        <button onClick={() => setParam("q", "")} aria-label="Remove" className="ml-1 text-brand-700">×</button>
                                    </span>
                                )}
                            </div>
                            <label className="inline-flex items-center gap-2 text-sm text-zinc-600">
                                <span>Sort</span>
                                <select
                                    value={sort}
                                    onChange={(e) => setParam("sort", e.target.value)}
                                    className="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-brand-500"
                                    data-testid="products-sort-select"
                                >
                                    {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                            </label>
                            <button
                                type="button"
                                onClick={() => setParam("smart", smart ? "" : "true")}
                                title={q ? "Re-rank results by likely intent using AI" : "Type a search query first to enable Smart Search"}
                                disabled={!q}
                                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                                    smart && q
                                        ? "border-brand-300 bg-brand-600 text-white shadow-sm"
                                        : "border-zinc-200 bg-white text-zinc-600 hover:border-brand-300 hover:text-brand-700"
                                } disabled:cursor-not-allowed disabled:opacity-50`}
                                data-testid="products-smart-search-toggle"
                                aria-pressed={smart}
                            >
                                <Sparkles className="h-3.5 w-3.5" />
                                Smart search
                                {smart && q && <span className="ml-1 text-[10px] uppercase opacity-80">ON</span>}
                            </button>
                        </div>

                        {loading ? (
                            <SkeletonGrid count={8} />
                        ) : data.items.length === 0 ? (
                            <EmptyState
                                title="No products found"
                                description="Try adjusting filters or searching with a different keyword."
                                action={<button onClick={() => setParams({}, { replace: false })} className="btn-brand">Reset filters</button>}
                            />
                        ) : (
                            <div className="grid grid-cols-2 gap-5 md:grid-cols-3" data-testid="products-grid">
                                {data.items.map((p) => <ProductCard key={p.id} product={p} />)}
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div className="mt-10 flex items-center justify-center gap-2" data-testid="products-pagination">
                                <button
                                    disabled={page <= 1}
                                    onClick={() => setParam("page", String(page - 1))}
                                    className="rounded-full border border-zinc-200 px-4 py-2 text-sm disabled:opacity-40 hover:border-brand-300"
                                    data-testid="pagination-prev-btn"
                                >
                                    Prev
                                </button>
                                <span className="px-3 text-sm text-zinc-600">Page {page} of {totalPages}</span>
                                <button
                                    disabled={page >= totalPages}
                                    onClick={() => setParam("page", String(page + 1))}
                                    className="rounded-full border border-zinc-200 px-4 py-2 text-sm disabled:opacity-40 hover:border-brand-300"
                                    data-testid="pagination-next-btn"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default Products;
